import React from "react";
import { Button, Tooltip } from "antd";
import {
  FormOutlined,
  HistoryOutlined,
  FileTextOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { SidePanelKind } from "./types";
import { useIsMobile } from "./useMediaQuery";

interface Props {
  tenderTitle: string;
  hasMessages: boolean;
  activePanel: SidePanelKind;
  onTogglePanel: (panel: Exclude<SidePanelKind, null>) => void;
  onNewChat: () => void;
  onExit?: () => void;
}

const ChatHeader: React.FC<Props> = ({
  tenderTitle,
  hasMessages,
  activePanel,
  onTogglePanel,
  onNewChat,
  onExit,
}) => {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isMobile
          ? `${t.space.s2}px ${t.space.s3}px`
          : `${t.space.s3}px ${t.space.s4}px`,
        borderBottom: `1px solid ${t.color.border}`,
        background: t.color.surface,
        gap: t.space.s2,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: t.space.s2,
          minWidth: 0,
          flex: 1,
        }}
      >
        <span
          style={{
            fontSize: isMobile ? 12 : t.font.size.md,
            fontWeight: 600,
            letterSpacing: isMobile ? 1.0 : 1.6,
            textTransform: "uppercase",
            color: t.color.text,
            whiteSpace: "nowrap",
          }}
        >
          {isMobile ? "ProcureX" : "ProcureX IntelliSuite"}
        </span>
        {!isMobile && (
          <span
            style={{
              fontSize: t.font.size.sm,
              color: t.color.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            · Tender {tenderTitle}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: t.space.s1, flexShrink: 0 }}>
        <Tooltip title={hasMessages ? "New chat" : "Already a new chat"}>
          <Button
            type="text"
            icon={<FormOutlined />}
            onClick={onNewChat}
            disabled={!hasMessages}
            style={{
              color: hasMessages ? t.color.textMuted : t.color.textSubtle,
              minWidth: isMobile ? 40 : undefined,
              height: isMobile ? 40 : undefined,
            }}
          >
            {!isMobile && "New"}
          </Button>
        </Tooltip>
        <Tooltip title="Documents">
          <Button
            type="text"
            icon={<FileTextOutlined />}
            onClick={() => onTogglePanel("documents")}
            style={{
              color:
                activePanel === "documents" ? t.color.accent : t.color.textMuted,
              background:
                activePanel === "documents" ? t.color.accentSoft : undefined,
              minWidth: isMobile ? 40 : undefined,
              height: isMobile ? 40 : undefined,
            }}
          />
        </Tooltip>
        <Tooltip title="Chat history">
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => onTogglePanel("history")}
            style={{
              color:
                activePanel === "history" ? t.color.accent : t.color.textMuted,
              background:
                activePanel === "history" ? t.color.accentSoft : undefined,
              minWidth: isMobile ? 40 : undefined,
              height: isMobile ? 40 : undefined,
            }}
          />
        </Tooltip>
        {onExit && (
          <Tooltip title="Close Ask AI">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onExit}
              style={{
                color: t.color.textMuted,
                marginLeft: t.space.s1,
                minWidth: isMobile ? 40 : undefined,
                height: isMobile ? 40 : undefined,
              }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ChatHeader;
